import React, { useState, useEffect, useCallback } from "react";
import { TextField, MenuItem, Button } from "@mui/material";
import { Row, Col, Card } from "react-bootstrap";
import { MyDiv, HeadingFour } from "../Common/Components";
import moment from "moment";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { ApproverDepartments, dateFormat } from "../Common/Constants/staticjson";
import CommonDataGrid from "../Common/commonDataGrid";
import { getRequest } from "../Common/api/apiService";
import { useDispatch, useSelector } from "react-redux";
import { projectTasksSlice } from "../ReduxToolkit/projectSlice.js";

const { setPagination, setSortModel, resetState ,resetPage} = projectTasksSlice.actions;

export default function ProjectTasks({ CoreProjectId }) {

  const [projectTask, setProjectTask] = useState([]);
  const [resourceData, setResourceData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchFilters, setSearchFilters] = useState({
    employee: "",
    department: "",
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD"),
  });



  const ranges = {
    Today: [moment(), moment()],
    Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
    "Last 7 Days": [moment().subtract(6, "days"), moment()],
    "Last 30 Days": [moment().subtract(29, "days"), moment()],
    "This Month": [moment().startOf("month"), moment().endOf("month")],
    "Last Month": [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
  };

  const dispatch = useDispatch();
  const paginationModel = useSelector(state => state.projectTasks.pagination);
  const sortModel = useSelector(state => state.projectTasks.sortModel);
  const searchText = useSelector(state => state.projectTasks.searchText);
  const [rowCount, setRowCount] = useState(0);


  const resetFilters = () => {
    setSearchFilters({
      employee: "",
      department: "",
      startDate: moment().startOf("month").format("YYYY-MM-DD"),
      endDate: moment().endOf("month").format("YYYY-MM-DD"),
    });
    dispatch(resetState());
  };



  const loadTasks = useCallback(async () => {
    setLoading(true);

    const { page, pageSize } = paginationModel;
    const sortField = sortModel[0]?.field || "tms_Task_Entry_Date";
    const sortOrder = sortModel[0]?.sort;
    const sortQuery = sortOrder ? `&sortField=${sortField}&sortOrder=${sortOrder}` : "";

    const { employee, department, startDate, endDate } = searchFilters;
    const query = `fetch-task-activities/${CoreProjectId}?page=${page}&limit=${pageSize}&dept=${department}&emp=${employee}&startDate=${startDate}&endDate=${endDate}${sortQuery}&query=${encodeURIComponent(searchText)}`;
    console.log("Request URL:", query);

    try {
      const response = await getRequest(query);
      if (response?.data) {
        setProjectTask(response.data.fetchedTasks || []);
        setRowCount(response.data.totalTasks || 0);
      }

    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, sortModel, searchFilters, CoreProjectId, searchText]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks, searchFilters]);


  const loadAllResources = useCallback(async () => {
    setLoading(true);
    const query = `fetchProjectResource`;

    try {
      const response = await getRequest(query);
      if (response?.data) {
        setResourceData(response.data || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);


  // Load tasks only on component mount
  useEffect(() => {
    loadAllResources();
  }, [loadAllResources]);


  const handleDateChange = picker => {
     dispatch(resetState());
    setSearchFilters({
      ...searchFilters,
      startDate: moment(picker.startDate).format("YYYY-MM-DD"),
      endDate: moment(picker.endDate).format("YYYY-MM-DD"),
    });
  };


  //data Grid
  const columns = [

    {
      field: "taskaddedemp",
      headerName: "Employee",
      flex: 1,
      renderCell: ({ row }) => <> {Array.isArray(row.taskaddedemp) ? row.taskaddedemp[0] : row.taskaddedemp || ""} </>
    },

    {
      field: "tms_Task_Department",
      headerName: "Department",
      flex: 1,
      renderCell: ({ row }) => <> {row.tms_Task_Department}</>
    },
    {
      field: "tms_Task_Worked_Hours",
      headerName: "Worked Hours",
      flex: 1,
      renderCell: ({ row }) => <> {row.tms_Task_Worked_Hours}</>
    },

    {
      field: "tms_Task_Entry_Date",
      headerName: "Worked Date",
      flex: 1,
      renderCell: ({ row }) => <>{moment(row.tms_Task_Entry_Date).format("MMMM DD YYYY")}</>
    }
  ]


  // --- Sorting & Pagination Handlers ---
  const handleSortModelChange = useCallback(
    model => {
      if (
        model.length === 0 ||
        model[0]?.field !== sortModel[0]?.field ||
        model[0]?.sort !== sortModel[0]?.sort
      ) {
        dispatch(setSortModel(model));
        dispatch(resetPage());
      }
    },
    [sortModel, dispatch]
  );

  const handlePaginationModelChange = newModel => {
    dispatch(setPagination(newModel));
  };



  return (
    <>
      <Card className="LayoutCard">
        <Card.Header>
          <Row>
            <Col md={3} className="SearchTextbox">
              <TextField
                className="w-100"
                select
                id="employee"
                label="Employee"
                value={searchFilters.employee}
                onChange={e => {
                  dispatch(resetState());
                  setSearchFilters({ ...searchFilters, employee: e.target.value })}}
                variant="standard">
                {resourceData.map(resources => (
                  <MenuItem key={resources._id} value={resources._id}>
                    {resources.emp_firstName}
                  </MenuItem>
                ))}
              </TextField>
            </Col>
            <Col md={3} className="SearchTextbox">
              <TextField
                className="w-100"
                select
                id="department"
                label="Department"
                value={searchFilters.department}
                onChange={e =>{ 
                   dispatch(resetState());
                  setSearchFilters({ ...searchFilters, department: e.target.value })}}
                variant="standard">
                {ApproverDepartments.map(resources => (
                  <MenuItem key={resources.value} value={resources.value}>
                    {resources.label}
                  </MenuItem>
                ))}
              </TextField>
            </Col>
            <Col md={2}>

              <DateRangePicker
                key={`${searchFilters.startDate}_${searchFilters.endDate}`} // Add this to force remount!
                onApply={(e, picker) => handleDateChange(picker)}
                initialSettings={{
                  ranges,
                  startDate: moment(searchFilters.startDate),
                  endDate: moment(searchFilters.endDate)
                }}
              >
                <input
                  type="text"
                  value={`${moment(searchFilters.startDate).format(dateFormat)} - ${moment(searchFilters.endDate).format(dateFormat)}`}
                  className="form-control data-range-picker"
                  readOnly
                />
              </DateRangePicker>


            </Col>
            <Col>
              <Button onClick={resetFilters} className="btn secondary-btn mt-2">
                Reset
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <MyDiv className="GeneralTable pxy-0 mt-0 ">
            {loading ? (
              <HeadingFour className="text-center">Loading...</HeadingFour>
            ) : (

              <Row className="GeneralTable">

                <CommonDataGrid
                  rows={projectTask || []}
                  columns={columns}
                  rowCount={rowCount}
                  loading={loading}
                  paginationModel={paginationModel}
                  onPaginationModelChange={handlePaginationModelChange}
                  sortModel={sortModel}
                  paginationMode="server"
                  onSortModelChange={handleSortModelChange}
                  getRowId={row => row._id}
                />

              </Row>
            )}

          </MyDiv>
        </Card.Body>
      </Card>
    </>
  );
}
